import {
  Activity,
  Calendar,
  CheckCircle,
  ChevronDown,
  CircleDot,
  Eye,
  ExternalLink,
  FolderGit2,
  GitBranch,
  GitFork,
  Github,
  Gitlab,
  Globe,
  Lock,
  type LucideIcon,
  Plug,
  Plus,
  RefreshCw,
  Search,
  Star,
  Tag,
  Unplug,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { api } from "../../lib/api";
import { createLogger } from "../../lib/logger";
import { notify } from "../../lib/notify";
import { useAppStore } from "../../store/useAppStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { CopyButton } from "../ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EmptyState } from "../ui/empty-state";
import { Input } from "../ui/input";
import { PageHeader } from "../ui/page-header";
import { Skeleton, SkeletonCard } from "../ui/skeleton";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const log = createLogger("IntegrationsDashboard");

// Bitbucket icon component (since it's not in lucide-react)
const BitbucketIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891zM14.52 15.53H9.522L8.17 8.466h7.561z" />
  </svg>
);

interface GitAccount {
  id: string;
  provider: string;
  username: string;
  email?: string;
  avatar_url?: string;
  display_name?: string;
  repository_count?: number;
  integration_count?: number;
  connected_at: string;
  last_synced_at?: string;
  is_active: boolean;
  sync_status?: string;
  repository_selection?: string; // "all" | "selected" (GitHub App)
  manage_url?: string; // GitHub page to edit the App's repository access
  auth_method?: string; // "app" | "oauth"
}

const PROVIDER_CONFIG = {
  github: {
    name: "GitHub",
    icon: Github,
    description: "Connect your GitHub repositories for seamless deployments",
    available: true,
  },
  gitlab: {
    name: "GitLab",
    icon: Gitlab,
    description: "Connect your GitLab repositories for seamless deployments",
    available: false,
  },
  bitbucket: {
    name: "Bitbucket",
    icon: BitbucketIcon,
    description: "Connect your Bitbucket repositories for seamless deployments",
    available: false,
  },
};

const PROVIDER_KEYS = Object.keys(PROVIDER_CONFIG) as Array<
  keyof typeof PROVIDER_CONFIG
>;

// Legacy fallback only — repo access is normally managed on the GitHub App's
// installation page (account.manage_url from the backend). This OAuth-era URL
// is used only when manage_url is missing (e.g. stale account records).
const GITHUB_APP_CONNECTIONS_URL = process.env.REACT_APP_GITHUB_CLIENT_ID
  ? `https://github.com/settings/connections/applications/${process.env.REACT_APP_GITHUB_CLIENT_ID}`
  : "https://github.com/settings/applications";

// Compact summary tile used in the header strip.
const SummaryTile = ({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  tone: string;
}) => (
  <Card>
    <CardContent className="flex items-center gap-3 p-4">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-h2 leading-none">{value}</div>
        <div className="mt-1 truncate text-xs text-muted-foreground">
          {label}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const IntegrationsDashboard: React.FC = () => {
  const [accounts, setAccounts] = useState<GitAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [repositories, setRepositories] = useState<Record<string, any[]>>({});
  const [activeProvider, setActiveProvider] = useState<string>("github");
  // Which account sub-tab is active within each provider (only used when a
  // provider has 2+ connected accounts).
  const [activeAccountByProvider, setActiveAccountByProvider] = useState<
    Record<string, string>
  >({});
  const [pendingDisconnectId, setPendingDisconnectId] = useState<string | null>(
    null
  );
  const [accountSearchTerms, setAccountSearchTerms] = useState<
    Record<string, string>
  >({});
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const { user } = useAppStore();
  // Whether the "full access (OAuth)" connect option is offered platform-wide
  // (admin-controlled). When true, connecting GitHub shows a method chooser.
  const [oauthConnectEnabled, setOauthConnectEnabled] = useState(false);
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [savingOAuthToggle, setSavingOAuthToggle] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = (await api.git.accounts.list()) as any;
      if (response.data && response.data.accounts) {
        setAccounts(response.data.accounts);

        // Auto-fetch repositories for every connected account.
        const accountList = response.data.accounts;
        for (const account of accountList) {
          try {
            const repoResponse = (await api.git.repositories.getByAccount(
              account.id
            )) as any;
            if (repoResponse.data && repoResponse.data.repositories) {
              setRepositories((prev) => ({
                ...prev,
                [account.id]: repoResponse.data.repositories,
              }));
            }
          } catch (repoError) {
            log.warn(
              `Failed to auto-fetch repositories for account ${account.username}`,
              repoError
            );
          }
        }
      } else {
        setAccounts([]);
      }
    } catch (error: any) {
      log.error("❌ Error fetching accounts", error);
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
    // Stable identity: depends only on stable setters/api. Must NOT depend on
    // `repositories` — it calls setRepositories, so that would recreate this
    // callback, re-trigger the mount effect, and loop (the "refreshes 2-3 times"
    // symptom).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAccounts();

    // Which connect methods does this platform offer?
    (async () => {
      try {
        const raw = (await api.git.getConfig()) as any;
        setOauthConnectEnabled(
          !!(raw.data?.github_oauth_connect_enabled ??
            raw.github_oauth_connect_enabled)
        );
      } catch (error) {
        log.error("Failed to load git config", error);
      }
    })();

    // Check for success parameter from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("connected") === "true") {
      // Tell the user whether a new account was added or an existing one was
      // refreshed. The latter happens when GitHub re-authorizes the account the
      // user is already signed into (GitHub has no "choose account" prompt).
      if (urlParams.get("already_connected") === "true") {
        notify.info("Account already connected", {
          description:
            "We refreshed your existing account. To add a different account, sign in to it on the provider first, then connect again.",
        });
      } else {
        notify.success("Account connected", {
          description: "Your Git account has been linked.",
        });
      }
      // Focus the provider tab that was just connected, if present.
      const provider = urlParams.get("provider");
      if (provider) setActiveProvider(provider);
      // Clear the URL parameter and refresh after a short delay
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        fetchAccounts();
      }, 1000);
    }
  }, [fetchAccounts]);

  const refreshAccounts = useCallback(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Entry point from Connect buttons. For GitHub, when the platform offers
  // both methods, a chooser dialog is shown first; startConnect() then runs
  // the selected flow.
  const handleConnect = async (provider: string) => {
    if (provider === "github" && oauthConnectEnabled) {
      setMethodDialogOpen(true);
      return;
    }
    await startConnect(provider, "app");
  };

  const startConnect = async (provider: string, method: "app" | "oauth") => {
    setMethodDialogOpen(false);
    try {
      setIsConnecting(true);

      if (provider === "github") {
        sessionStorage.setItem("oauth_provider", provider);
        const response = (await api.git.connect(provider, {
          redirect_uri: `${window.location.origin}/integrations/callback`,
          scopes: ["repo", "user:email", "read:user", "admin:repo_hook"],
          method,
        })) as any;

        if (response.data?.oauth_url) {
          window.location.href = response.data.oauth_url;
        }
      } else {
        notify.info("Coming soon", {
          description: `${
            PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG]?.name
          } integration coming soon! We're starting with GitHub for now.`,
        });
      }
    } catch (error) {
      log.error(`Error connecting to ${provider}`, error);
      notify.error("Connection failed", {
        description: `Failed to connect to ${provider}. Please try again.`,
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSync = async (accountId: string) => {
    setSyncingIds((prev) => new Set(prev).add(accountId));
    try {
      await api.git.accounts.sync(accountId);
      notify.success("Sync started", {
        description: "Refreshing repositories from the provider.",
      });
      // Drop the cached repos so the refetch repopulates from the new sync
      setRepositories((prev) => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
      await fetchAccounts();
    } catch (error) {
      log.error("Error syncing account", error);
      notify.error("Sync failed", {
        description: "Failed to sync account. Please try again.",
      });
    } finally {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  const handleDisconnect = async (accountId: string) => {
    setPendingDisconnectId(null);

    try {
      await api.git.accounts.disconnect(accountId, {
        revoke_token: true,
        confirm: true,
      });
      refreshAccounts();
      setRepositories((prev) => {
        const newRepos = { ...prev };
        delete newRepos[accountId];
        return newRepos;
      });
    } catch (error) {
      log.error("Error disconnecting account", error);
      notify.error("Disconnect failed", {
        description: "Failed to disconnect account. Please try again.",
      });
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const getProviderIcon = (provider: string) => {
    const config = PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG];
    return config?.icon || Github;
  };

  const getProviderName = (provider: string) => {
    const config = PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG];
    return config?.name || provider;
  };

  // Active account sub-tab for a provider, falling back to the first account
  // (handles the selected account being disconnected).
  const getActiveAccountId = (provider: string, list: GitAccount[]) => {
    const current = activeAccountByProvider[provider];
    return current && list.some((a) => a.id === current)
      ? current
      : list[0]?.id;
  };

  const filterRepositories = (repos: any[], term: string) => {
    if (!term.trim()) return repos;
    const q = term.toLowerCase();
    return repos.filter(
      (repo) =>
        repo.name?.toLowerCase().includes(q) ||
        repo.full_name?.toLowerCase().includes(q) ||
        repo.description?.toLowerCase().includes(q) ||
        repo.language?.toLowerCase().includes(q)
    );
  };

  const handleAccountSearchChange = (accountId: string, value: string) => {
    setAccountSearchTerms((prev) => ({ ...prev, [accountId]: value }));
  };

  // One connected-account card. Rendered inside the active provider's tab.
  const renderAccountCard = (account: GitAccount) => {
    const ProviderIcon = getProviderIcon(account.provider);
    const providerName = getProviderName(account.provider);
    const repoCount =
      repositories[account.id]?.length ?? account.repository_count ?? 0;
    const accountQuery = accountSearchTerms[account.id] || "";
    const visibleRepos = filterRepositories(
      repositories[account.id] || [],
      accountQuery
    );
    const isSyncing =
      syncingIds.has(account.id) ||
      account.sync_status === "syncing" ||
      account.sync_status === "pending";

    return (
      <Card key={account.id}>
        <CardContent className="space-y-4 p-5">
          {/* Account header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={account.avatar_url} alt={account.username} />
                <AvatarFallback>
                  <ProviderIcon className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">
                    {account.display_name || account.username}
                  </span>
                  <Badge variant="outline" className="gap-1">
                    <ProviderIcon className="h-3 w-3" />
                    {providerName}
                  </Badge>
                  {account.provider === "github" &&
                    (account.auth_method === "oauth" ? (
                      <Badge
                        variant="secondary"
                        className="gap-1 bg-warning/10 text-warning hover:bg-warning/10"
                      >
                        <Globe className="h-3 w-3" />
                        OAuth · Full access
                      </Badge>
                    ) : account.auth_method === "app" ? (
                      <Badge variant="secondary" className="gap-1">
                        <Lock className="h-3 w-3" />
                        GitHub App
                        {account.repository_selection === "selected"
                          ? " · Selected repos"
                          : account.repository_selection === "all"
                            ? " · All repos"
                            : ""}
                      </Badge>
                    ) : null)}
                </div>
                <p className="truncate text-sm text-muted-foreground">
                  @{account.username}
                  {account.email ? ` · ${account.email}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isSyncing ? (
                <Badge variant="secondary" className="gap-1">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Syncing
                </Badge>
              ) : (
                <Badge className="gap-1 bg-success/10 text-success hover:bg-success/10">
                  <CheckCircle className="h-3 w-3" />
                  Connected
                </Badge>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={syncingIds.has(account.id)}
                      onClick={() => handleSync(account.id)}
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${
                          syncingIds.has(account.id) ? "animate-spin" : ""
                        }`}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Sync repositories</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingDisconnectId(account.id)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Unplug className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </div>
          </div>

          {/* Account stats */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-muted/40 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FolderGit2 className="h-3.5 w-3.5" />
                Repositories
              </div>
              <div className="mt-1 text-h3">{repoCount}</div>
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Activity className="h-3.5 w-3.5" />
                Integrations
              </div>
              <div className="mt-1 text-h3">
                {account.integration_count || 0}
              </div>
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Connected
              </div>
              <div className="mt-1 text-sm font-medium">
                {formatDate(account.connected_at)}
              </div>
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <RefreshCw className="h-3.5 w-3.5" />
                Last sync
              </div>
              <div className="mt-1 text-sm font-medium">
                {account.last_synced_at
                  ? formatDate(account.last_synced_at)
                  : "Never"}
              </div>
            </div>
          </div>

          {/* Repositories — always visible */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-h5">
              Repositories
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                {visibleRepos.length}
              </span>
            </h4>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search repositories in this account…"
                value={accountSearchTerms[account.id] || ""}
                onChange={(e) =>
                  handleAccountSearchChange(account.id, e.target.value)
                }
                className="pl-9"
              />
            </div>

            {visibleRepos.length > 0 ? (
              <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
                {visibleRepos.map((repo: any) => (
                  <div
                    key={repo.id}
                    className="rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {repo.private ? (
                            <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          ) : (
                            <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate text-sm font-medium">
                            {repo.full_name || repo.name}
                          </span>
                          {repo.archived && (
                            <Badge variant="outline" className="text-2xs">
                              Archived
                            </Badge>
                          )}
                        </div>
                        {repo.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {repo.description}
                          </p>
                        )}

                        {/* Stats */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          {repo.language && (
                            <span className="inline-flex items-center gap-1">
                              <span className="h-2 w-2 rounded-full bg-primary" />
                              {repo.language}
                            </span>
                          )}
                          {repo.star_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Star className="h-3.5 w-3.5" />
                              {repo.star_count}
                            </span>
                          )}
                          {repo.fork_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <GitFork className="h-3.5 w-3.5" />
                              {repo.fork_count}
                            </span>
                          )}
                          {repo.watchers_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Eye className="h-3.5 w-3.5" />
                              {repo.watchers_count}
                            </span>
                          )}
                          {repo.open_issues_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <CircleDot className="h-3.5 w-3.5" />
                              {repo.open_issues_count}
                            </span>
                          )}
                          {repo.default_branch && (
                            <span className="inline-flex items-center gap-1">
                              <GitBranch className="h-3.5 w-3.5" />
                              {repo.default_branch}
                            </span>
                          )}
                          {repo.topics && repo.topics.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Tag className="h-3.5 w-3.5" />
                              {repo.topics.length} topics
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        {repo.clone_url && (
                          <CopyButton
                            value={repo.clone_url}
                            label="Copy clone URL"
                            size="icon"
                            className="h-7 w-7"
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Open repository"
                          onClick={() =>
                            window.open(repo.url, "_blank", "noopener")
                          }
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : accountSearchTerms[account.id] ? (
              <div className="rounded-lg bg-card border-dashed py-8 text-center text-sm text-muted-foreground">
                No repositories match your search.
              </div>
            ) : (
              <div className="space-y-3 rounded-lg bg-card border-dashed p-6 text-center">
                <FolderGit2 className="mx-auto h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">No repositories found</p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                    Avahana only sees the repositories you granted when
                    installing the GitHub App. Add repositories on GitHub, then
                    sync.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {account.provider === "github" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(
                          account.manage_url || GITHUB_APP_CONNECTIONS_URL,
                          "_blank",
                          "noopener"
                        )
                      }
                    >
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                      Manage repository access
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={syncingIds.has(account.id)}
                    onClick={() => handleSync(account.id)}
                  >
                    <RefreshCw
                      className={`mr-2 h-3.5 w-3.5 ${
                        syncingIds.has(account.id) ? "animate-spin" : ""
                      }`}
                    />
                    Sync
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleOAuthToggle = async (enabled: boolean) => {
    setSavingOAuthToggle(true);
    const previous = oauthConnectEnabled;
    setOauthConnectEnabled(enabled);
    try {
      await adminApi.updateGitSettings({
        github_oauth_connect_enabled: enabled,
      });
      notify.success(
        enabled
          ? "Full-access (OAuth) connections enabled"
          : "Full-access (OAuth) connections disabled",
        {
          description: enabled
            ? "Users can now choose OAuth when connecting GitHub."
            : "New connections must use the GitHub App. Existing OAuth accounts keep working.",
        }
      );
    } catch (error) {
      setOauthConnectEnabled(previous);
      log.error("Failed to update git settings", error);
      notify.error("Could not update setting", {
        description: "Only platform admins can change this.",
      });
    } finally {
      setSavingOAuthToggle(false);
    }
  };

  const header = (
    <PageHeader
      eyebrow="Workspace"
      title="Integrations"
      description="Connect your Git providers to enable seamless deployments"
      actions={
      user?.role === "admin" && (
        <div className="flex items-center gap-2 rounded-lg bg-card px-3 py-2">
          <div>
            <p className="text-sm font-medium">Allow full-access (OAuth)</p>
            <p className="text-xs text-muted-foreground">
              Admin · offer OAuth alongside the GitHub App
            </p>
          </div>
          <Switch
            checked={oauthConnectEnabled}
            disabled={savingOAuthToggle}
            onCheckedChange={handleOAuthToggle}
          />
        </div>
      )
      }
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {header}
        {/* Summary tiles */}
        <div className="grid gap-4 sm:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        {/* Provider tab bar */}
        <div className="flex gap-6 border-b pb-2">
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-28" />
        </div>
        {/* Toolbar + account card */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-9 w-44" />
        </div>
        <SkeletonCard />
      </div>
    );
  }

  const totalRepositories = accounts.reduce(
    (sum, account) =>
      sum + (repositories[account.id]?.length ?? account.repository_count ?? 0),
    0
  );
  const totalIntegrations = accounts.reduce(
    (sum, account) => sum + (account.integration_count || 0),
    0
  );

  // Group accounts by provider so each provider tab shows only its accounts.
  const groupedAccounts = accounts.reduce<Record<string, GitAccount[]>>(
    (acc, account) => {
      (acc[account.provider] ||= []).push(account);
      return acc;
    },
    {}
  );

  return (
    <div className="space-y-6">
      {header}

      {accounts.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryTile
            icon={Plug}
            label="Connected accounts"
            value={accounts.length}
            tone="bg-success/10 text-success"
          />
          <SummaryTile
            icon={FolderGit2}
            label="Total repositories"
            value={totalRepositories}
            tone="bg-primary/10 text-primary"
          />
          <SummaryTile
            icon={Activity}
            label="Active integrations"
            value={totalIntegrations}
            tone="bg-info/10 text-info"
          />
        </div>
      )}

      {/* Provider tabs — one per supported provider; the connect action lives
          inside each tab now (no global "Connect provider" button). */}
      <Tabs
        value={activeProvider}
        onValueChange={setActiveProvider}
        className="space-y-4"
      >
        <TabsList className="h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent p-0">
          {PROVIDER_KEYS.map((key) => {
            const config = PROVIDER_CONFIG[key];
            const Icon = config.icon;
            const count = groupedAccounts[key]?.length ?? 0;
            return (
              <TabsTrigger
                key={key}
                value={key}
                className="gap-2 rounded-none border-b-2 border-transparent px-4 py-2.5 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                <Icon className="h-4 w-4" />
                {config.name}
                {count > 0 && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold">
                    {count}
                  </span>
                )}
                {!config.available && (
                  <span className="text-2xs font-medium uppercase tracking-wide opacity-70">
                    Soon
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {PROVIDER_KEYS.map((key) => {
          const config = PROVIDER_CONFIG[key];
          // BitbucketIcon is a custom component; EmptyState's icon prop expects a
          // LucideIcon. Both render as <Icon className=… />, so the cast is safe.
          const Icon = config.icon as LucideIcon;
          const providerAccounts = groupedAccounts[key] ?? [];
          const activeAccountId = getActiveAccountId(key, providerAccounts);

          return (
            <TabsContent key={key} value={key} className="space-y-4">
              {!config.available ? (
                <EmptyState
                  icon={Icon}
                  title={`${config.name} coming soon`}
                  description={`${config.name} integration isn't available yet — we're starting with GitHub.`}
                />
              ) : providerAccounts.length === 0 ? (
                <EmptyState
                  icon={Icon}
                  title={`No ${config.name} accounts connected`}
                  description={config.description}
                  primaryAction={{
                    label: isConnecting
                      ? "Connecting…"
                      : `Connect ${config.name}`,
                    icon: Icon,
                    onClick: () => handleConnect(key),
                  }}
                />
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">
                      {providerAccounts.length}{" "}
                      {providerAccounts.length === 1 ? "account" : "accounts"}{" "}
                      connected.
                      {key === "github" &&
                        " To add a different account, sign in to it on github.com first (or use a separate browser profile)."}
                    </p>
                    {key === "github" && oauthConnectEnabled ? (
                      // Both connect methods offered → pick from a dropdown.
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="shrink-0"
                            disabled={isConnecting}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {isConnecting
                              ? "Connecting…"
                              : `Add ${config.name} account`}
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-80">
                          <DropdownMenuItem
                            className="flex-col items-start gap-0.5 py-2.5"
                            onClick={() => startConnect("github", "app")}
                          >
                            <span className="flex items-center gap-2 font-medium">
                              <Lock className="h-4 w-4" />
                              GitHub App
                              <Badge className="bg-success/10 text-success hover:bg-success/10">
                                Recommended
                              </Badge>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              You pick exactly which repositories to grant.
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="flex-col items-start gap-0.5 py-2.5"
                            onClick={() => startConnect("github", "oauth")}
                          >
                            <span className="flex items-center gap-2 font-medium">
                              <Globe className="h-4 w-4" />
                              GitHub OAuth
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Full access — includes repos shared with you and
                              org repos.
                            </span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Button
                        variant="outline"
                        className="shrink-0"
                        disabled={isConnecting}
                        onClick={() => handleConnect(key)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {isConnecting
                          ? "Connecting…"
                          : `Add ${config.name} account`}
                      </Button>
                    )}
                  </div>
                  {providerAccounts.length === 1 ? (
                    // Single account: no nested tabs, just the card.
                    renderAccountCard(providerAccounts[0])
                  ) : (
                    // Multiple accounts: one sub-tab per account.
                    <Tabs
                      value={activeAccountId}
                      onValueChange={(v) =>
                        setActiveAccountByProvider((prev) => ({
                          ...prev,
                          [key]: v,
                        }))
                      }
                      className="space-y-4"
                    >
                      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                        {providerAccounts.map((account) => {
                          const accountSyncing =
                            syncingIds.has(account.id) ||
                            account.sync_status === "syncing" ||
                            account.sync_status === "pending";
                          const repoCount =
                            repositories[account.id]?.length ??
                            account.repository_count ??
                            0;
                          return (
                            <TabsTrigger
                              key={account.id}
                              value={account.id}
                              className="h-auto gap-2.5 rounded-lg bg-card px-3 py-2 data-[state=active]:border-primary data-[state=active]:bg-primary/5 "
                            >
                              <Avatar className="h-7 w-7">
                                <AvatarImage
                                  src={account.avatar_url}
                                  alt={account.username}
                                />
                                <AvatarFallback>
                                  <Icon className="h-3.5 w-3.5" />
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col items-start leading-tight">
                                <span className="max-w-[12rem] truncate text-sm font-medium">
                                  {account.display_name || account.username}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {accountSyncing
                                    ? "Syncing…"
                                    : `${repoCount} ${
                                        repoCount === 1 ? "repo" : "repos"
                                      }`}
                                </span>
                              </div>
                              {accountSyncing && (
                                <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                              )}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                      {providerAccounts.map((account) => (
                        <TabsContent key={account.id} value={account.id}>
                          {renderAccountCard(account)}
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}
                </>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <AlertDialog
        open={!!pendingDisconnectId}
        onOpenChange={(open) => {
          if (!open) setPendingDisconnectId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect this account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Disconnecting will:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Revoke Avahana's access on the provider (GitHub).</li>
                  <li>
                    Remove this account's synced repositories and integrations.
                  </li>
                  <li>
                    Require you to re-authorize if you reconnect later (you'll
                    see the consent screen again).
                  </li>
                </ul>
                <p>This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                pendingDisconnectId && handleDisconnect(pendingDisconnectId)
              }
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* GitHub connect-method chooser (shown when OAuth is enabled) */}
      <Dialog open={methodDialogOpen} onOpenChange={setMethodDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>How do you want to connect GitHub?</DialogTitle>
            <DialogDescription>
              Both methods let Avahana clone and deploy your repositories —
              they differ in how much access you grant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              className="w-full rounded-lg p-4 text-left transition-colors hover:border-primary hover:bg-muted/50"
              disabled={isConnecting}
              onClick={() => startConnect("github", "app")}
            >
              <div className="flex items-center gap-2 font-medium">
                <Lock className="h-4 w-4" />
                Install the GitHub App
                <Badge className="bg-success/10 text-success hover:bg-success/10">
                  Recommended
                </Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                You choose exactly which repositories to grant — Avahana can
                never see the rest. Edit the selection on GitHub anytime.
              </p>
            </button>
            <button
              type="button"
              className="w-full rounded-lg p-4 text-left transition-colors hover:border-primary hover:bg-muted/50"
              disabled={isConnecting}
              onClick={() => startConnect("github", "oauth")}
            >
              <div className="flex items-center gap-2 font-medium">
                <Globe className="h-4 w-4" />
                Connect with full access (OAuth)
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Grants read access to <b>all</b> repositories your account can
                reach — including repos shared with you and organization repos.
                Use this when you need to deploy a repo someone shared with
                you.
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
